import React, { useEffect, useMemo, useState } from "react";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import BusyOverlay from "../components/common/BusyOverlay";
import { usePageTitle } from "../contexts/PageTitleContext";
import { paymentService } from "../services/api/payment.service";
import type { Payment } from "../types/payment.types";
import {
  downloadReportPdf,
  printReportPdf,
  getReportPdfBase64,
} from "../utils/reportPdf";
import EmailReportModal from "../components/common/EmailReportModal";
import "../styles/pages/Reports.css";

const getLast30Days = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split("T")[0];
};

const getToday = () => new Date().toISOString().split("T")[0];

const MONTH_ABBREVIATIONS = [
  "Jan.",
  "Feb.",
  "Mar.",
  "Apr.",
  "May",
  "Jun.",
  "Jul.",
  "Aug.",
  "Sep.",
  "Oct.",
  "Nov.",
  "Dec.",
];

const formatCreatedAt = (value?: string): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const month = MONTH_ABBREVIATIONS[date.getMonth()];
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

const getStringField = (
  source: Record<string, unknown>,
  keys: string[],
): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const getPaymentCategory = (item: Payment): string => {
  const row = item as Payment & {
    category?: string;
    categoryName?: string;
    applicationCategory?: string;
    certificateApplicationCategory?: string;
    certificateApplication?: Record<string, unknown>;
  };

  const directCategory = getStringField(
    row as unknown as Record<string, unknown>,
    [
      "category",
      "categoryName",
      "applicationCategory",
      "certificateApplicationCategory",
    ],
  );
  if (directCategory) {
    return directCategory;
  }

  const app = row.certificateApplication;
  if (app && typeof app === "object") {
    const nestedCategory = getStringField(app, [
      "category",
      "categoryName",
      "applicationCategory",
      "certificateApplicationCategory",
    ]);
    if (nestedCategory) {
      return nestedCategory;
    }
  }

  return "";
};

const getPaymentCurrency = (item: Payment): string => {
  const rawCurrency = (item as Payment & { currency?: unknown }).currency;
  const withCurrency = item as Payment & {
    currencyCode?: string;
    currencyName?: string;
  };

  if (typeof rawCurrency === "string") {
    return rawCurrency;
  }

  if (rawCurrency && typeof rawCurrency === "object") {
    const currencyObj = rawCurrency as { code?: string; name?: string };
    return currencyObj.code || currencyObj.name || "";
  }

  return withCurrency.currencyCode || withCurrency.currencyName || "";
};

const RevenueCollectionReport: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filters, setFilters] = useState({
    startDate: getLast30Days(),
    endDate: getToday(),
    paymentMode: "",
    category: "",
    currency: "",
  });

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Revenue Collection Report",
      "Track collected revenue by mode, category, and period",
    );
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await paymentService.getPayments();
        setPayments(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err?.message || "Failed to load revenue report");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [setPageTitle]);

  const paymentModes = useMemo(
    () =>
      [
        ...new Set(payments.map((item) => item.paymentMode).filter(Boolean)),
      ].sort(),
    [payments],
  );

  const currencies = useMemo(
    () =>
      [
        ...new Set(
          payments.map((item) => getPaymentCurrency(item)).filter(Boolean),
        ),
      ].sort(),
    [payments],
  );

  const categories = useMemo(
    () =>
      [
        ...new Set(
          payments.map((item) => getPaymentCategory(item)).filter(Boolean),
        ),
      ].sort(),
    [payments],
  );

  const filteredRows = useMemo(() => {
    return payments.filter((item) => {
      const created = item.createdAt
        ? new Date(item.createdAt).toISOString().split("T")[0]
        : "";
      const matchesDate =
        (!filters.startDate || (created && created >= filters.startDate)) &&
        (!filters.endDate || (created && created <= filters.endDate));

      const matchesMode =
        !filters.paymentMode || item.paymentMode === filters.paymentMode;

      const category = getPaymentCategory(item);
      const matchesCategory =
        !filters.category ||
        category.toLowerCase() === filters.category.toLowerCase();

      const currency = getPaymentCurrency(item);
      const matchesCurrency =
        !filters.currency ||
        currency.toLowerCase() === filters.currency.toLowerCase();

      return matchesDate && matchesMode && matchesCategory && matchesCurrency;
    });
  }, [payments, filters]);

  const metrics = useMemo(() => {
    const totalLra = filteredRows.reduce(
      (sum, item) => sum + (Number(item.lraPayment) || 0),
      0,
    );
    const totalOperational = filteredRows.reduce(
      (sum, item) => sum + (Number(item.operationalCost) || 0),
      0,
    );
    const totalRevenue = totalLra + totalOperational;
    const uniqueApplications = new Set(
      filteredRows.map((item) => item.certificateApplicationId),
    ).size;

    return { totalLra, totalOperational, totalRevenue, uniqueApplications };
  }, [filteredRows]);

  const [showEmailModal, setShowEmailModal] = useState(false);

  const buildPdfConfig = () => {
    const rows = filteredRows.map((item) => {
      const total =
        (Number(item.lraPayment) || 0) + (Number(item.operationalCost) || 0);
      return [
        formatCreatedAt(item.createdAt),
        getPaymentCategory(item) || "-",
        item.paymentMode || "-",
        getPaymentCurrency(item) || "-",
        String(Number(item.lraPayment) || 0),
        String(Number(item.operationalCost) || 0),
        String(total),
        item.paymentReferenceNumber || item.revenueNumber || "-",
      ];
    });

    return {
      title: "Revenue Collection Report",
      filters: [
        { label: "Start Date", value: filters.startDate },
        { label: "End Date", value: filters.endDate },
        { label: "Payment Mode", value: filters.paymentMode || "All" },
        { label: "Category", value: filters.category || "All" },
        { label: "Currency", value: filters.currency || "All" },
      ],
      columns: [
        "Created At",
        "Category",
        "Payment Mode",
        "Currency",
        "LRA",
        "Operational",
        "Total",
        "Reference",
      ],
      rows,
    };
  };

  const handleDownloadPdf = () => {
    downloadReportPdf("revenue-collection-report.pdf", buildPdfConfig());
  };

  const handlePrintPdf = () => {
    printReportPdf(buildPdfConfig());
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="reports-page">
          <BusyOverlay
            isVisible={loading}
            message="Loading revenue report..."
          />
          {error ? <div className="alert alert-error">{error}</div> : null}

          <div className="reports-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handlePrintPdf}
              disabled={filteredRows.length === 0}
            >
              Print PDF
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleDownloadPdf}
              disabled={filteredRows.length === 0}
            >
              Download PDF
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setShowEmailModal(true)}
              disabled={filteredRows.length === 0}
            >
              ✉️ Email
            </button>
          </div>

          <EmailReportModal
            open={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            getAttachmentBase64={() => getReportPdfBase64(buildPdfConfig())}
            fileName="revenue-collection-report.pdf"
            defaultSubject="Revenue Collection Report"
            defaultMessage="Please find the attached Revenue Collection Report."
          />

          <div className="reports-filters">
            <div className="filter-group">
              <label className="filter-label">Start Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">End Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Payment Mode</label>
              <select
                className="filter-select"
                value={filters.paymentMode}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    paymentMode: e.target.value,
                  }))
                }
              >
                <option value="">All Payment Modes</option>
                {paymentModes.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Category</label>
              <select
                className="filter-select"
                value={filters.category}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, category: e.target.value }))
                }
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Currency</label>
              <select
                className="filter-select"
                value={filters.currency}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, currency: e.target.value }))
                }
              >
                <option value="">All Currencies</option>
                {currencies.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="reports-kpis">
            <div className="kpi-card">
              <span className="kpi-label">Applications Paid</span>
              <span className="kpi-value">{metrics.uniqueApplications}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">LRA Total</span>
              <span className="kpi-value">
                {metrics.totalLra.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Operational Total</span>
              <span className="kpi-value">
                {metrics.totalOperational.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Combined Revenue</span>
              <span className="kpi-value">
                {metrics.totalRevenue.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <div className="reports-table-container">
            <div className="table-wrapper">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Created At</th>
                    <th>Category</th>
                    <th>Payment Mode</th>
                    <th>Currency</th>
                    <th>LRA Payment</th>
                    <th>Operational Cost</th>
                    <th>Total</th>
                    <th>Reference No.</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((item) => {
                    const rowTotal =
                      (Number(item.lraPayment) || 0) +
                      (Number(item.operationalCost) || 0);
                    return (
                      <tr
                        key={
                          item.id ||
                          `${item.certificateApplicationId}-${item.createdAt}`
                        }
                      >
                        <td>{formatCreatedAt(item.createdAt)}</td>
                        <td>{getPaymentCategory(item) || "-"}</td>
                        <td>{item.paymentMode || "-"}</td>
                        <td>{getPaymentCurrency(item) || "-"}</td>
                        <td>
                          {(Number(item.lraPayment) || 0).toLocaleString(
                            undefined,
                            { maximumFractionDigits: 2 },
                          )}
                        </td>
                        <td>
                          {(Number(item.operationalCost) || 0).toLocaleString(
                            undefined,
                            { maximumFractionDigits: 2 },
                          )}
                        </td>
                        <td>
                          {rowTotal.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td>
                          {item.paymentReferenceNumber ||
                            item.revenueNumber ||
                            "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default RevenueCollectionReport;
